import { gettext } from '@eventbrite/i18n';
import React from 'react';

export type EventDescriptionProps = {
    summary: string;
    shouldShowSalesEndedMessage: boolean | null;
    organizerSalesEndedMessage: string | null;
    salesEndedMessage: string | null;
    performers: Array<Performer>;
    hasStructuredContent: boolean;
    structuredContent: StructuredContent;
};

type Performer = {
    name: string;
    biography: string | null;
    image_url: string | null;
};

type StructuredContent = {
    modules: StructuredContentModule[];
    admat: Admat | null;
};

export enum StructuredContentModuleType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video',
}

type StructuredContentModule = {
    type: StructuredContentModuleType;
} & (TextContentProps | ImageContentProps | VideoContentProps);

export enum TextContentAlignment {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

type TextContentProps = {
    text: string;
    alignment: TextContentAlignment;
};

type ImageContentProps = {
    url: string;
    altText: string;
    captionText?: string;
};

type VideoContentProps = {
    url: string;
    altText: string;
};

type Admat = {
    imageUrl: string;
    altText: string;
};

const getTextAlignment = (alignment: TextContentAlignment) => {
    switch (alignment) {
        case TextContentAlignment.LEFT:
            return 'l-align-left';
        case TextContentAlignment.CENTER:
            return 'l-align-center';
        case TextContentAlignment.RIGHT:
            return 'l-align-right';
    }
};

const TextContent = ({ text, alignment }: TextContentProps) => (
    <div
        className={`structured-content-rich-text structured-content__module ${getTextAlignment(
            alignment,
        )} l-mar-vert-6 l-sm-mar-vert-4 text-body-medium`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: text }}
    />
);

const VideoContent = ({ url, altText }: VideoContentProps) => (
    <div className="structured-content-video structured-content__module l-mar-vert-6 l-sm-mar-vert-4 text-body-medium">
        <iframe
            className="structured-content-video__iframe"
            src={url}
            frameBorder="0"
            allowFullScreen
            title={altText}
        >
            {altText}
        </iframe>
    </div>
);

const ImageContent = ({ url, altText, captionText }: ImageContentProps) => (
    <div className="structured-content__module l-mar-vert-6 l-sm-mar-vert-4 text-body-medium">
        <img
            className="structured-content__image g-img"
            src={url}
            alt={altText}
        />
        {captionText && (
            <span className="text-body-small text-body--faint">
                {captionText}
            </span>
        )}
    </div>
);

const Admat = ({ imageUrl, altText }: Admat) => (
    <div className="structured-content__module l-mar-bot-6 l-sm-mar-bot-4 text-body-large">
        <img
            className="structured-content__image g-img"
            src={imageUrl}
            alt={altText}
            data-automation="admat"
        />
    </div>
);

const StructuredModuleRenderer = ({
    module,
}: {
    module: StructuredContentModule;
}) => {
    switch (module.type) {
        case StructuredContentModuleType.TEXT:
            const textContent = module as TextContentProps;
            return (
                <TextContent
                    text={textContent.text}
                    alignment={textContent.alignment}
                />
            );
        case StructuredContentModuleType.IMAGE:
            const imageContent = module as ImageContentProps;
            return (
                <ImageContent
                    url={imageContent.url}
                    altText={imageContent.altText}
                    captionText={imageContent.captionText}
                />
            );
        case StructuredContentModuleType.VIDEO:
            const videoContent = module as VideoContentProps;
            return (
                <VideoContent
                    url={videoContent.url}
                    altText={videoContent.altText}
                />
            );
        default:
            return <></>;
    }
};

const OrganizerSalesEndedMessage = ({
    organizerSalesEndedMessage,
    salesEndedMessage,
}: Pick<
    EventDescriptionProps,
    'organizerSalesEndedMessage' | 'salesEndedMessage'
>) => (
    <div className="g-group l-lg-mar-bot-6 l-md-mar-bot-4 l-pad-bot-6 hide-small">
        <div className="g-cell g-cell-1-1">
            <h2 className="is-hidden-accessible">
                {gettext('Sales Have Ended')}
            </h2>
            <div className="label-primary">{organizerSalesEndedMessage}</div>
            <div className="l-mar-top-1" data-automation="sales-ended-message">
                {salesEndedMessage}
            </div>
        </div>
    </div>
);

export const EventDescription = ({
    shouldShowSalesEndedMessage,
    organizerSalesEndedMessage,
    salesEndedMessage,
    hasStructuredContent,
    summary,
    performers,
    structuredContent: { modules = [], admat },
}: EventDescriptionProps) => (
    <>
        {shouldShowSalesEndedMessage && (
            <OrganizerSalesEndedMessage
                organizerSalesEndedMessage={organizerSalesEndedMessage}
                salesEndedMessage={salesEndedMessage}
            />
        )}
        {summary && (
            <div
                className={
                    !hasStructuredContent
                        ? 'g-group l-lg-mar-bot-6 l-md-mar-bot-4'
                        : 'g-group l-lg-mar-bot-6 l-md-mar-bot-4 l-pad-bot-6'
                }
            >
                <div className="g-cell g-cell-2-12 show-small">
                    <i className="ico-document ico--medium"></i>
                    <span className="is-hidden-accessible">
                        {gettext('Event description')}
                    </span>
                </div>
                <div className="g-cell g-cell-10-12 g-cell-md-1-1">
                    {!hasStructuredContent && (
                        <h3 className="label-primary">
                            {gettext('Description')}
                        </h3>
                    )}
                    <div className="has-user-generated-content">
                        {hasStructuredContent ? (
                            <div
                                className="text-body-medium"
                                data-automation="listing-event-description"
                            >
                                <strong>{summary}</strong>
                            </div>
                        ) : (
                            <div
                                className="l-mar-top-3"
                                data-automation="listing-event-description"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: summary }}
                            ></div>
                        )}
                    </div>
                </div>
            </div>
        )}
        {hasStructuredContent && (
            <div className="g-group l-mar-bot-6 l-sm-mar-bot-4">
                <div className="g-cell g-cell-2-12 show-small">
                    {!summary && (
                        <>
                            <i className="ico-document ico--medium"></i>
                            <span className="is-hidden-accessible">
                                {gettext('About this Event')}
                            </span>
                        </>
                    )}
                </div>
                <div className="structured-content g-cell g-cell-10-12 g-cell-md-1-1">
                    {admat && (
                        <Admat
                            imageUrl={admat.imageUrl}
                            altText={admat.altText}
                        />
                    )}

                    <h2 className="text-body-large hide-small">
                        {gettext('About this event')}
                    </h2>

                    <div
                        className="has-user-generated-content"
                        data-automation="about-this-event-sc"
                    >
                        {modules.map((module, index) => (
                            <StructuredModuleRenderer
                                module={module}
                                key={index}
                            />
                        ))}
                    </div>
                </div>
            </div>
        )}
        {performers.length > 0 && (
            <div className="eds-l-mar-bot-6 eds-l-sm-mar-bot-4 has-user-generated-content">
                <ul>
                    {performers.map((performer, index) => (
                        <li key={index}>
                            <div className="eds-l-mar-top-6 eds-l-mar-bot-1 eds-l-sm-mar-vert-4 eds-text-bl ">
                                {performer.name}
                            </div>
                            {performer.biography && (
                                <div className="structured-content-rich-text structured-content__module eds-l-mar-vert-1 eds-l-sm-mar-vert-4 text-body-medium">
                                    <p>
                                        <span>{performer.biography}</span>
                                    </p>
                                </div>
                            )}
                            {performer.image_url && (
                                <div className="structured-content__module eds-l-mar-vert-1 eds-l-sm-mar-vert-4 eds-text-bm">
                                    <img
                                        className="structured-content__image g-img"
                                        src={performer.image_url}
                                        alt={performer.name}
                                    />
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </>
);
